// src/mobile_pages/process/upload.jsx

import { useState, useEffect } from "react";
import {
  uploadProcessFile,
  getUpdateRunList,
  deleteUpdateRun,
} from "../../api/process";
import ConfirmModal from "../../components/common/ui/ConfirmModal";
import { formatDate } from "../../utils/date";

export default function Upload() {
  const [file, setFile] = useState(null);
  const [withBaseline, setWithBaseline] = useState(false);
  const [baselineCode, setBaselineCode] = useState("");
  const [loading, setLoading] = useState(false);

  const [actualRuns, setActualRuns] = useState([]);
  const [baselineRuns, setBaselineRuns] = useState([]);
  const [cutoffDate, setCutoffDate] = useState("");
  const [tab, setTab] = useState("A");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetRun, setTargetRun] = useState(null);
  const [sourceType, setSourceType] = useState("P6");
  const [acEqBa, setAcEqBa] = useState(false);
  const [showForm, setShowForm] = useState(true);

  const handleDeleteClick = (run) => {
    setTargetRun(run);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!targetRun) return;
    try {
      await deleteUpdateRun(targetRun.id);
      alert("삭제되었습니다.");
      loadHistory();
    } catch (e) {
      alert("삭제 실패: " + e.message);
    } finally {
      setConfirmOpen(false);
      setTargetRun(null);
    }
  };

  const currentRuns = tab === "A" ? actualRuns : baselineRuns;

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const runs = await getUpdateRunList();
      const actual = runs.filter((r) => r.run_type === "A");
      const baseline = runs.filter((r) => r.run_type === "B");
      setActualRuns(actual.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)));
      setBaselineRuns(baseline.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)));
    } catch (e) {
      console.error("이력 로드 실패:", e);
    }
  };

  const handleUpload = async () => {
    if (!file) return alert("파일을 선택하세요.");
    if (withBaseline && !baselineCode.trim()) return alert("Baseline 버전명을 입력하세요.");

    setLoading(true);
    try {
      await uploadProcessFile(file, withBaseline, baselineCode, cutoffDate, sourceType, acEqBa);
      alert("업로드가 완료되었습니다.");
      loadHistory();
      setFile(null);
      setBaselineCode("");
      setCutoffDate("");
    } catch (e) {
      alert(`에러 발생: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-4 mobile-bottom-spacing">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-4 border">
          <div>
            <h2 className="text-lg font-bold text-gray-900">공정 실적 업로드</h2>
            <p className="text-gray-500 text-xs mt-0.5">
              실적 {actualRuns.length}건 · 베이스라인 {baselineRuns.length}건
            </p>
          </div>
        </div>

        {/* 업로드 폼 */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <button
            onClick={() => setShowForm(!showForm)}
            className="w-full px-4 py-3 flex items-center justify-between"
          >
            <span className="text-sm font-medium text-gray-900">새 파일 업로드</span>
            <span className={`text-gray-400 transition-transform ${showForm ? "rotate-90" : ""}`}>›</span>
          </button>

          {showForm && (
            <div className="px-4 pb-4 border-t space-y-4">
              {/* 파일 타입 선택 */}
              <div className="pt-4">
                <label className="text-xs font-medium text-gray-600 mb-2 block">파일 타입</label>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setSourceType("P6")}
                    className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${sourceType === "P6" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                      }`}
                  >
                    Primavera P6
                  </button>
                  <button
                    onClick={() => setSourceType("MS")}
                    className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${sourceType === "MS" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                      }`}
                  >
                    MS Project
                  </button>
                </div>
              </div>

              {/* 파일 선택 */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">공정 파일 (XML)</label>
                <label className="flex items-center justify-center w-full px-4 py-3 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                  <span className="text-sm text-gray-600">
                    {file ? file.name : "파일 선택..."}
                  </span>
                  <input
                    type="file"
                    accept=".xml"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
              </div>

              {/* 실적 기준일 */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">실적 기준일</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={cutoffDate}
                  onChange={(e) => setCutoffDate(e.target.value)}
                />
              </div>

              {/* Baseline 포함 여부 */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-700">Baseline 포함</span>
                <button
                  onClick={() => {
                    setWithBaseline(!withBaseline);
                    if (!withBaseline) setAcEqBa(false);
                  }}
                  className={`w-12 h-6 rounded-full transition-colors ${withBaseline ? "bg-blue-600" : "bg-gray-200"
                    }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${withBaseline ? "translate-x-6" : "translate-x-0.5"
                    }`} />
                </button>
              </div>

              {/* Actual 내용을 Baseline에도 저장 옵션 */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-700">Actual → Baseline 저장</span>
                <button
                  onClick={() => {
                    setAcEqBa(!acEqBa);
                    if (!acEqBa) setWithBaseline(false);
                  }}
                  className={`w-12 h-6 rounded-full transition-colors ${acEqBa ? "bg-blue-600" : "bg-gray-200"
                    }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${acEqBa ? "translate-x-6" : "translate-x-0.5"
                    }`} />
                </button>
              </div>

              {/* Baseline 버전명 */}
              {(withBaseline || acEqBa) && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-2 block">Revision</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 font-medium">Rev.</span>
                    <input
                      type="text"
                      value={baselineCode}
                      onChange={(e) => setBaselineCode(e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="버전명 입력"
                    />
                  </div>
                </div>
              )}

              {/* 업로드 버튼 */}
              <button
                disabled={loading || !file}
                onClick={handleUpload}
                className="btn-save w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "업로드 중..." : "업로드"}
              </button>
            </div>
          )}
        </div>

        {/* 업로드 이력 */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {/* 탭 헤더 */}
          <div className="p-3 border-b">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setTab("A")}
                className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${tab === "A" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                  }`}
              >
                실적 ({actualRuns.length})
              </button>
              <button
                onClick={() => setTab("B")}
                className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${tab === "B" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                  }`}
              >
                베이스라인 ({baselineRuns.length})
              </button>
            </div>
          </div>

          {/* 이력 리스트 */}
          <div className="max-h-80 overflow-y-auto">
            {currentRuns.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-gray-500">
                  {tab === "A" ? "실적 업로드 이력이 없습니다." : "베이스라인 이력이 없습니다."}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {currentRuns.map((row) => (
                  <div key={row.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900">
                          {tab === "A"
                            ? row.data_date
                              ? formatDate(row.data_date)
                              : "기준일 미입력"
                            : `Rev.${row.revision || "-"}`}
                        </h4>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                          <span>{row.uploaded_by?.name || "-"}</span>
                          <span className="text-gray-400">
                            {row.uploaded_at ? formatDate(row.uploaded_at) : "-"}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteClick(row)}
                        className="btn-delete-sm text-xs"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 삭제 확인 모달 */}
        <ConfirmModal
          open={confirmOpen}
          title="삭제 확인"
          description={
            targetRun
              ? `선택한 ${targetRun.run_type === "A" ? "실적" : "기준선"} 데이터를 삭제하시겠습니까?`
              : ""
          }
          cancelText="아니오"
          confirmText="예"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmOpen(false)}
          onClose={() => setConfirmOpen(false)}
        />
      </div>
    </div>
  );
}
